import { Action } from 'redux';

import { NODE_TYPES, GEN_AI_TILESET_COMBO_ID } from 'modules/workflows/constants';
import { Node, NodeData, NodeType, Workflow } from 'modules/workflows/types';
import {
  fixType,
  getNodeTypeFromNode,
  parseBackendNodes,
  parseNode,
  sendNodeUtils,
  validateNode,
  hydrateJourneyVisitorCountState,
} from 'modules/workflows/utils';
import {
  AddNodeAction,
  AddNodeErrorsAction,
  AddNodesAction,
  CloneNodesAction,
  CreateWorkflowDraftResponseAction,
  DeleteNodeAction,
  DeleteNodesAction,
  DeselectNodeAction,
  DeselectNodesAction,
  FetchMetricsResponseAction,
  FetchVisitorCountsResponseAction,
  FetchWorkflowResponseAction,
  SaveWorkflowDraftResponseAction,
  SelectNodesAction,
  SelectGenAINodesAction,
  UpdateNodeAction,
  UpdateNodeComboIdAction,
  UpdateNodesAction,
  JourneyVisitorMetricsResponseAction,
  WorkflowViewAction,
} from '../../actions';
import { WorkflowState } from '../../ducks.types';

export type NodeState = WorkflowState['nodes'];

/** Actions called directly after fetching a workflow from the backend */
type FetchWorkflowResponseActions =
  | CreateWorkflowDraftResponseAction
  | FetchWorkflowResponseAction
  | SaveWorkflowDraftResponseAction;

const doesIdExist = (id: string | number | null | undefined): id is number | string =>
  id !== null && id !== undefined;

/**
 * Parses the nodes coming from a backend call fetching a workflow
 */
export const setInitialNodes = (
  state: NodeState,
  action: FetchWorkflowResponseActions,
  wfState: WorkflowState,
) => {
  const { workflow, shouldValidateNodes } = action.payload || {};
  const { nodes = [], notes = [] } = workflow;

  // Parse the nodes, then map them to an object with the node id as the key
  return parseBackendNodes(
    { nodes, notes, shouldValidate: shouldValidateNodes },
    { ...wfState, workflow },
  ).reduce((acc, node) => ({ ...acc, [node.id]: node }), {});
};

export const addNode = (state: NodeState, action: AddNodeAction, wfState: WorkflowState) => {
  const { node } = action.payload;
  const { nextNodeId: id } = wfState;

  return {
    ...state,
    [id]: parseNode({ ...(node || {}), id }, wfState),
  };
};

export const addNodes = (
  state: NodeState,
  action: AddNodesAction | CloneNodesAction,
  wfState: WorkflowState,
) => {
  const { nodes = [] } = action.payload;
  const { nextNodeId } = wfState;
  let currNextNodeId = nextNodeId;

  return nodes.reduce(
    (acc, node) => {
      // parses to a valid number if node is being cloned from an existing one
      // but resolves to null if an entirely new node
      const clonedFromId = Number(node.id) || null;

      const updatedAcc = {
        ...acc,
        [currNextNodeId]: parseNode(
          {
            ...node,
            id: currNextNodeId,

            // attach clonedFromId field for nodes that are being cloned
            // so it is possible to associate them to the correct edge(s)
            // in following action for cloning edges
            ...(clonedFromId && { clonedFromId }),
          },
          wfState,
        ),
      };

      currNextNodeId -= 1;

      return updatedAcc;
    },
    { ...state },
  );
};

export const cloneNodes = (state: NodeState, action: CloneNodesAction, wfState: WorkflowState) => {
  const { nodes = [] } = action.payload;
  const { nextNodeId } = wfState;

  // first, add cloned nodes
  const updatedNodes = addNodes(state, action, wfState);

  // just cloned nodes have the most negative keys/ids
  const highestKey = nextNodeId; // first key that will be added
  const lowestKey = highestKey - nodes.length + 1; // last key that will be added

  // then, update node selections
  return Object.entries(updatedNodes).reduce(
    (acc, [key, value]) => {
      const keyNum = parseInt(key, 10);
      const isNew = keyNum >= lowestKey && keyNum <= highestKey;

      return isNew
        ? // select all newly cloned nodes
          // and add slight offset
          {
            ...acc,
            [key]: {
              ...value,
              isSelected: true,
              x: value.x + 10,
              y: value.y + 10,
              liveWorkflowNodeId: null,
            },
          }
        : // deselect everything else
          { ...acc, [key]: { ...value, isSelected: false } };
    },
    { ...updatedNodes },
  );
};

export const deleteNode = (state: NodeState, action: DeleteNodeAction) => {
  const { nodeId } = action.payload;
  const untypedId = fixType(nodeId);
  return Object.values(state).reduce(
    (acc, node) =>
      untypedId === fixType(node?.id)
        ? acc
        : // deselect all remaining nodes
          { ...acc, [node?.id]: { ...node, isSelected: false } },
    {},
  );
};

export const deleteNodes = (state: NodeState, action: Omit<DeleteNodesAction, 'type'>) => {
  const { nodeIds = [] } = action.payload;
  const untypedNodeIds = nodeIds.map(fixType);
  return Object.values(state).reduce(
    (acc, node) =>
      untypedNodeIds.includes(fixType(node?.id))
        ? acc
        : // deselect all remaining nodes
          { ...acc, [node?.id]: { ...node, isSelected: false } },
    {},
  );
};

export const selectNodes = (state: NodeState, action: SelectNodesAction) => {
  const { nodeIds } = action.payload;

  /**
   * Runs through every node so that it will unselect any nodes which were previously selected
   * TODO: check with large set of nodes during https://iterable.atlassian.net/browse/WF-356
   */
  return Object.entries(state).reduce((acc, [id, node]) => {
    return {
      ...acc,
      [id]: {
        ...node,
        isSelected: nodeIds.includes(id),
      },
    };
  }, {});
};

export const selectGenAINodes = (state: NodeState, action: SelectGenAINodesAction) => {
  const { nodeIds } = action.payload;

  return Object.entries(state).reduce((acc, [id, node]) => {
    const isAIGenerated = nodeIds.includes(id);
    return {
      ...acc,
      [id]: {
        ...node,
        // Unset isAIGenerated and comboId if not included in nodeIds
        comboId: isAIGenerated ? GEN_AI_TILESET_COMBO_ID : undefined,
        isAIGenerated: isAIGenerated || undefined,
      },
    };
  }, {});
};

export const deselectNodes = (state: NodeState, action: DeselectNodesAction) => {
  const { nodeIds } = action.payload;

  return Object.entries(state).reduce(
    (acc, [id, node]) =>
      nodeIds.includes(id)
        ? {
            ...acc,
            [id]: { ...node, isSelected: false },
          }
        : { ...acc, [id]: { ...node } },
    {},
  );
};

export const deselectNode = (
  state: NodeState,
  action: DeselectNodeAction,
  wfState: WorkflowState,
) => {
  const nodeId = action.payload.nodeId || wfState.openedNodeId;
  if (!nodeId) return state;
  const node = state[nodeId];
  if (node) {
    return { ...state, [nodeId]: { ...state[nodeId], isSelected: false } };
  }
  return state;
};

export const updateNode = (
  state: NodeState,
  action: Omit<UpdateNodeAction, 'type'>,
  wfState: WorkflowState,
) => {
  const { node = {}, nodeId, options } = action.payload;
  const { shouldValidate = true } = options || {};
  const { openedNodeId, workflow } = wfState;
  const id = nodeId || node?.id || openedNodeId;
  const isStartNode = id === `${workflow?.startWorkflowNodeId}`;
  const shouldValidateFieldsMatchNodes = shouldValidate && wfState.isYesNoSplitDataToggleEnabled;

  if (!doesIdExist(id)) return state;

  const parsedNode = parseNode({ ...(state[id] || {}), ...node, id }, wfState, {
    shouldValidate,
  });

  // If the user is editing the start node of a workflow with fields match nodes,
  // we need to validate those nodes based on the start node's data.
  if (isStartNode && shouldValidateFieldsMatchNodes) {
    return Object.entries(state).reduce((acc, [nId, n]) => {
      let currentNode;

      switch (n.type) {
        case NODE_TYPES.FIELDS_MATCH:
          currentNode = validateNode(n, { startNode: parsedNode });
          break;
        case NODE_TYPES.START:
          currentNode = parsedNode;
          break;
        default:
          currentNode = n;
      }

      return { ...acc, [nId]: currentNode };
    }, {});
  }

  return { ...state, [id]: parsedNode };
};

export const addNodeErrors = (state: NodeState, action: AddNodeErrorsAction) => {
  const { nodeIds, hasError } = action.payload;

  return Object.entries(state).reduce(
    (acc, [id, node]) =>
      nodeIds.includes(id)
        ? {
            ...acc,
            [id]: { ...node, hasError },
          }
        : { ...acc, [id]: { ...node } },
    {},
  );
};

export const removeNodeError = (state: NodeState, action: Action, wfState: WorkflowState) => {
  const { openedNodeId } = wfState;

  if (!doesIdExist(openedNodeId)) return state;

  return {
    ...state,
    [openedNodeId]: { ...state[openedNodeId], hasError: false },
  };
};

export const updateNodeComboId = (state: NodeState, action: UpdateNodeComboIdAction) => {
  const { nodeIds, comboId, parentId } = action.payload;

  return Object.entries(state).reduce(
    (acc, [id, node]) =>
      nodeIds.includes(id)
        ? {
            ...acc,
            [id]: { ...node, comboId, parentId },
          }
        : { ...acc, [id]: { ...node } },
    {},
  );
};

export const updateNodes = (
  state: NodeState,
  action: Omit<UpdateNodesAction, 'type'>,
  wfState: WorkflowState,
) => {
  const { nodes = [] } = action.payload;
  return nodes.reduce(
    (acc, node) => ({
      ...acc,
      [node.id as string]: parseNode({ ...acc[node.id as string], ...node }, wfState),
    }),
    { ...state },
  );
};

export const updateStickyNotesVisibility = (
  isNotesVisible: boolean,
  state: NodeState,
  action: Action,
  wfState: WorkflowState,
) =>
  updateNodes(state, { payload: { nodes: Object.values(state) } }, { ...wfState, isNotesVisible });

type CampaignIdsToMetrics = FetchMetricsResponseAction['payload']['campaignIdsToMetrics'];

export const createNodeDescriptionFooter = ({
  campaignIdsToMetrics,
  nodeType,
  campaignId,
  wfState,
}: {
  campaignIdsToMetrics: CampaignIdsToMetrics;
  nodeType: NodeType;
  campaignId: number;
  wfState: WorkflowState;
}) => {
  const { isMetricsSelectionEnabled = false, workflow } = wfState;
  const { tileMetrics } = workflow || {};
  const metrics = campaignIdsToMetrics?.[campaignId];
  return sendNodeUtils.getDescFooterSegments({
    nodeType,
    metrics,
    campaignId,
    isMetricsSelectionEnabled,
    selectedMetrics: tileMetrics,
  });
};

interface AddAnalyticsFooterArgs {
  campaignIdsToMetrics: CampaignIdsToMetrics;
  nodeType: NodeType;
  nodeData: NodeData;
  wfState: WorkflowState;
}

const addAnalyticsToNodeFooter = ({
  campaignIdsToMetrics,
  nodeType,
  nodeData,
  wfState,
}: AddAnalyticsFooterArgs) => {
  const nodeDescFooter = createNodeDescriptionFooter({
    campaignIdsToMetrics,
    nodeType,
    campaignId: nodeData.campaignId,
    wfState,
  });
  return {
    ...nodeData,
    textSegments: { ...nodeData.textSegments, nodeDescFooter },
  };
};

const addAnalyticsToNode = ({
  campaignIdsToMetrics,
  nodeType,
  nodeData,
  wfState,
}: AddAnalyticsFooterArgs) => {
  if (nodeType !== NODE_TYPES.CHANNEL_OPT) {
    return addAnalyticsToNodeFooter({ campaignIdsToMetrics, nodeType, nodeData, wfState });
  }

  const updatedChildNodes = (nodeData?.childNodes || []).map((childNode) => ({
    ...childNode,
    nodeData: addAnalyticsToNodeFooter({
      campaignIdsToMetrics,
      nodeType: childNode.type,
      nodeData: childNode.nodeData,
      wfState,
    }),
  }));
  return {
    ...nodeData,
    childNodes: updatedChildNodes,
  };
};

export const setSendMetrics = (
  state: NodeState,
  action: FetchMetricsResponseAction,
  wfState: WorkflowState,
): NodeState => {
  const { campaignIdsToMetrics = {} } = action.payload;
  const nodes = Object.values(state);

  const nodesMap = nodes.reduce((acc, node) => {
    if (!sendNodeUtils.isSendNode(node.type)) return { ...acc, [node.id]: node };

    const { nodeData, type: nodeType } = node;
    const updatedNodeData = addAnalyticsToNode({
      campaignIdsToMetrics,
      nodeType,
      nodeData,
      wfState,
    });
    return {
      ...acc,
      [node.id]: parseNode({ ...node, nodeData: updatedNodeData }, wfState),
    };
  }, {});

  return nodesMap;
};

export const setClearVisitorCounts = (state: NodeState, wfState: WorkflowState) => {
  const visitorTileMetrics = {
    showVisitorCounts: false,
    visitorsExitedCount: undefined,
    visitorsEnteredCount: undefined,
    edgeVisitorPercentages: undefined,
  };
  const nodesMap = Object.values(state).reduce((acc, node) => {
    return {
      ...acc,
      [node.id]: parseNode({ ...node, ...visitorTileMetrics }, wfState),
    };
  }, {});
  return nodesMap;
};

// For NumUser
export const setVisitorCounts = (
  state: NodeState,
  action: FetchVisitorCountsResponseAction,
  wfState: WorkflowState,
) => {
  const { countsData = [] } = action.payload;
  const nodes = Object.values(state);

  const visitorCountMap = countsData.reduce(
    (acc, { workflowNodeId, count }) => ({
      ...acc,
      [workflowNodeId]: count,
    }),
    {},
  ) as Record<string, number>;

  const nodesMap = nodes.reduce((acc, node) => {
    return {
      ...acc,
      [node.id]: parseNode({ ...node, numUsers: visitorCountMap[node.id] }, wfState),
    };
  }, {});

  return nodesMap;
};
// For Journey Visitor Metrics
export const setVisitorMetrics = (
  state: NodeState,
  action: JourneyVisitorMetricsResponseAction,
  wfState: WorkflowState,
) => {
  const {
    journeyVisitorMetrics: { tileVisitorMetrics },
  } = action.payload;
  const visitorCountMap = tileVisitorMetrics.reduce(
    (
      acc,
      { nodeId: workflowNodeId, visitorsExitedCount, visitorsEnteredCount, edgeVisitorPercentages },
    ) => ({
      ...acc,
      [workflowNodeId]: {
        visitorsExitedCount,
        visitorsEnteredCount,
        edgeVisitorPercentages,
      },
    }),
    {},
  ) as Record<
    string,
    {
      visitorsExitedCount: number;
      visitorsEnteredCount: number;
      edgeVisitorPercentages: {
        srcNodeId: number;
        destNodeId: number;
        srcOutputIdx: number;
        percentageOfVisitors: number;
      }[];
    }
  >;
  const nodesMap = Object.values(state).reduce((acc, node) => {
    const visitorTileMetrics = visitorCountMap?.[node.id];
    return {
      ...acc,
      [node.id]: parseNode({ ...node, ...visitorTileMetrics }, wfState),
    };
  }, {});
  return nodesMap;
};

export const setOriginalNodes = (state: Node[], action: FetchWorkflowResponseActions) => {
  return (action.payload?.workflow?.nodes || []).map((node) => ({
    type: getNodeTypeFromNode(node),
    nodeData: node.nodeData,
  }));
};

export const updateNodesOnWorkflowViewChange = (
  state: NodeState,
  action: WorkflowViewAction,
  wfState: WorkflowState,
) => {
  const workflowView = action.payload.workflowView;
  const workflow = wfState?.workflow;
  const isInProgressTotalPublishedView = wfState?.isInProgressTotalPublishdViewEnabled;
  const workflowType = (workflow as Workflow)?.workflowType;
  const journeyVisitorCountsState = hydrateJourneyVisitorCountState(
    isInProgressTotalPublishedView,
    workflowView,
    workflowType,
  );

  const newNodeState = (Object.entries(state || {}) || []).reduce((acc, [key, value]) => {
    return { ...acc, [key]: { ...value, ...journeyVisitorCountsState } };
  }, {});

  return newNodeState;
};
/* -------------------------------------------------------------------------- */
/* Next Node Id                                                               */
/* -------------------------------------------------------------------------- */

export const INITIAL_NODE_ID = -1;

export const incrementNextNodeId = (state: number) => state - 1;
export const incrementNextNodeIdBy = (state: number, action: { payload: { num: number } }) => {
  const { num } = action.payload;
  return Number.isFinite(num) ? state - num : state;
};
export const setNextNodeId = (state: number, action: { payload: { id: number } }) =>
  action.payload.id;
